import { useEffect, useMemo, useRef, useState } from 'react';
import { useLookup, type LookupItem } from '../lookups';

/**
 * Editor for spoken text on voicebot nodes (Mod_VB_Say.Text, Mod_VB_Ask
 * PromptText / ProcessingText).
 *
 *  - multiline textarea that grows with its content
 *  - "Insert variable" picker: drops `${Name}` at the caret, using the
 *    application's variable list (same names the Lua interpolation resolves
 *    via spCustomerApplicationVariable_List; a leading # or $ is optional)
 *  - placeholder chips under the field: every `${...}` in the text, green
 *    when it resolves to a known variable, red when it doesn't
 *
 * The value is committed on blur and right after an insert, as plain text.
 */
export function PromptTextEditor({
  value,
  readOnly,
  commit,
  applicationId,
  placeholder,
}: {
  value: string;
  readOnly: boolean;
  commit: (v: string) => void;
  applicationId: number;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const { items: variables, loading } = useLookup('variable', applicationId);

  // Auto-grow: keep at least 3 rows, expand to fit the content.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 72)}px`;
  }, [local]);

  const known = useMemo(() => {
    const set = new Set<string>();
    for (const v of variables) set.add(normalizeName(v.name));
    return set;
  }, [variables]);

  const placeholders = useMemo(() => {
    const out: Array<{ raw: string; ok: boolean }> = [];
    const seen = new Set<string>();
    const re = /\$\{([^}]+)\}/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(local)) !== null) {
      const raw = m[1].trim();
      if (!raw || seen.has(raw)) continue;
      seen.add(raw);
      out.push({ raw, ok: known.has(normalizeName(raw)) });
    }
    return out;
  }, [local, known]);

  const insertVariable = (name: string) => {
    const token = '${' + name + '}';
    const el = ref.current;
    const start = el?.selectionStart ?? local.length;
    const end = el?.selectionEnd ?? local.length;
    const next = local.slice(0, start) + token + local.slice(end);
    setLocal(next);
    commit(next);
    // Put the caret right after the inserted token once React has rendered.
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="fb-prompt">
      <textarea
        ref={ref}
        className="fb-textarea fb-prompt-textarea"
        rows={3}
        value={local}
        readOnly={readOnly}
        placeholder={placeholder}
        spellCheck={false}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (!readOnly && local !== value) commit(local);
        }}
      />
      <div className="fb-prompt-tools">
        {!readOnly && (
          <select
            className="fb-input fb-prompt-insert"
            value=""
            disabled={loading}
            onChange={(e) => {
              const v = e.target.value;
              if (v) insertVariable(v);
            }}
            title="Insert a ${variable} placeholder at the cursor"
          >
            <option value="">{loading ? 'Loading variables…' : '+ Insert variable…'}</option>
            {groupVariables(variables).map((g) =>
              g.category ? (
                <optgroup key={g.category} label={g.category}>
                  {g.items.map((it) => (
                    <option key={String(it.id)} value={it.name.trim()}>
                      {it.name.trim()}
                    </option>
                  ))}
                </optgroup>
              ) : (
                g.items.map((it) => (
                  <option key={String(it.id)} value={it.name.trim()}>
                    {it.name.trim()}
                  </option>
                ))
              ),
            )}
          </select>
        )}
        <span className="fb-prompt-hint">
          {placeholders.length === 0
            ? 'Use ${Name} to speak a variable, e.g. "Dus je bent in ${City}".'
            : 'Placeholders:'}
        </span>
        {placeholders.map((p) => (
          <span
            key={p.raw}
            className={`fb-prompt-chip${p.ok ? ' fb-prompt-chip--ok' : ' fb-prompt-chip--bad'}`}
            title={p.ok ? 'Resolves to a variable of this application' : 'No variable with this name — it will be spoken as empty'}
          >
            {'${' + p.raw + '}'}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Case-insensitive, leading #/$ stripped — mirrors ssVB_InterpolateVars. */
function normalizeName(name: string): string {
  return name.trim().toUpperCase().replace(/^[#$]+/, '');
}

function groupVariables(items: LookupItem[]): Array<{ category: string | null; items: LookupItem[] }> {
  const groups: Array<{ category: string | null; items: LookupItem[] }> = [];
  for (const it of items) {
    const cat = it.category ?? null;
    const last = groups[groups.length - 1];
    if (last && last.category === cat) last.items.push(it);
    else groups.push({ category: cat, items: [it] });
  }
  return groups;
}
